import { useState, HTMLAttributes } from 'react';
import { useForm, isNotEmpty } from '@mantine/form';

import { RequestAccessValues } from '../form';
import { InputWrapper, ScrollArea } from '@mantine/core';
import { Combobox, Group, InputBase, Text, useCombobox } from '@mantine/core';
import { NextAction } from '../next-action';
import { Header } from '../header';

import EmailIcon from './integration-icons/email.svg?react';
import PowerAutomateIcon from './integration-icons/power-automate.svg?react';
import ZapierIcon from './integration-icons/zapier.svg?react';
import ManualUploadIcon from './integration-icons/manual.svg?react';
import ApiIcon from './integration-icons/api.svg?react';
import UiPathIcon from './integration-icons/uipath.svg?react';
import BluePrismIcon from './integration-icons/blueprism.svg?react';
import WebhookIcon from './integration-icons/webhook.svg?react';
import SourceIcon from './integration-icons/source.svg?react';
import DestinationIcon from './integration-icons/destination.svg?react';
import Step from './step';

interface Item {
  icon: React.ReactNode;
  value: string;
  group?: string;
  description: string;
}

const iconSize = { height: '1.5rem' };

const sources: Item[] = [
  {
    icon: <EmailIcon {...iconSize} />,
    value: 'Email',
    description: 'Forward documents to an auto-generated email address.',
  },
  {
    icon: <ApiIcon {...iconSize} />,
    value: 'REST API',
    description: 'Upload documents using the Cradl AI REST API directly.',
  },
  {
    icon: <ManualUploadIcon {...iconSize} />,
    value: 'Manual upload',
    description: 'Upload documents manually in the web interface.',
  },
  {
    group: 'Automation platforms',
    icon: <PowerAutomateIcon {...iconSize} />,
    value: 'PowerAutomate',
    description: 'Upload from Power Automate',
  },
  {
    group: 'Automation platforms',
    icon: <ZapierIcon {...iconSize} />,
    value: 'Zapier',
    description: 'Upload from Power Automate',
  },
  {
    group: 'Automation platforms',
    icon: <UiPathIcon {...iconSize} />,
    value: 'UiPath',
    description: 'Upload documents from UiPath',
  },
  {
    group: 'Automation platforms',
    icon: <BluePrismIcon {...iconSize} />,
    value: 'BluePrism',
    description: 'Upload from BluePrism',
  },
];

const destinations: Item[] = [
  {
    icon: <WebhookIcon {...iconSize} />,
    value: 'Webhook',
    description: 'Forward documents to an auto-generated email address.',
  },
  {
    icon: <ApiIcon {...iconSize} />,
    value: 'REST API',
    description: 'Upload documents using the Cradl AI REST API directly.',
  },
  {
    group: 'Automation platforms',
    icon: <PowerAutomateIcon {...iconSize} />,
    value: 'PowerAutomate',
    description: 'Upload from Power Automate',
  },
  {
    group: 'Automation platforms',
    icon: <ZapierIcon {...iconSize} />,
    value: 'Zapier',
    description: 'Upload from Power Automate',
  },
  {
    group: 'Automation platforms',
    icon: <UiPathIcon {...iconSize} />,
    value: 'UiPath',
    description: 'Upload documents from UiPath',
  },
  {
    group: 'Automation platforms',
    icon: <BluePrismIcon {...iconSize} />,
    value: 'BluePrism',
    description: 'Upload from BluePrism',
  },
];

function SelectOption({ icon, value, description }: Item) {
  return (
    <Group className="tw-px-1 tw-py-2">
      <span>{icon}</span>
      <div>
        <Text fz="sm" fw={500}>
          {value}
        </Text>
        <Text fz="xs" opacity={0.6}>
          {description}
        </Text>
      </div>
    </Group>
  );
}

type IntegrationSelectProps = {
  integrations: Item[];
  onSelected: (value: string) => void;
  placeholder: Item;
  defaultValue?: string;
};

export function IntegrationSelect({ integrations, defaultValue, onSelected, placeholder }: IntegrationSelectProps) {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const [value, setValue] = useState<string | undefined>(defaultValue);
  const selectedOption = integrations.find((item) => item.value === value);

  const groups = [...new Set(integrations.map((v) => v.group))];

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={(val) => {
        onSelected(val);
        setValue(val);
        combobox.closeDropdown();
      }}
      withinPortal
    >
      <Combobox.Target>
        <InputBase
          component="button"
          type="button"
          pointer
          rightSection={<Combobox.Chevron />}
          onClick={() => combobox.toggleDropdown()}
          rightSectionPointerEvents="none"
          multiline
          radius="0.8rem"
        >
          {selectedOption ? <SelectOption {...selectedOption} /> : <SelectOption {...placeholder} />}
        </InputBase>
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>
          <ScrollArea.Autosize mah={200} type="scroll">
            {groups.map((group) => (
              <Combobox.Group label={group} key={group}>
                {integrations
                  .filter((item) => item.group === group)
                  .map((item) => (
                    <Combobox.Option value={item.value} key={item.value}>
                      <SelectOption {...item} description={''} />
                    </Combobox.Option>
                  ))}
              </Combobox.Group>
            ))}
          </ScrollArea.Autosize>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}

export type RequestAccessFormStepValues = {
  source: string;
  destination: string;
};

export type RequestAccessFormStepProps = {
  currentValues: Partial<RequestAccessValues>;
  goToNextStep: (data: any) => void;
  goToPrevStep: (data?: any) => void;
} & HTMLAttributes<HTMLDivElement>;

export default function ({ goToNextStep, goToPrevStep, currentValues, ...rest }: RequestAccessFormStepProps) {
  const form = useForm({
    mode: 'uncontrolled',
    initialValues: currentValues,
    validate: {
      source: isNotEmpty('This field is mandatory'),
      destination: isNotEmpty('This field is mandatory'),
    },
  });

  return (
    <Step {...rest}>
      <Header
        title="How would you like to integrate Cradl AI?"
        subtitle="We'll use this to setup you account. You can change this later."
        size="md"
      />

      <form onSubmit={form.onSubmit(goToNextStep)} className="tw-flex tw-flex-col tw-gap-4">
        <InputWrapper error={form.errors.source} classNames={{ root: 'tw-flex tw-flex-col tw-gap-2' }}>
          <IntegrationSelect
            integrations={sources}
            defaultValue={form.getValues().source}
            onSelected={(value: string) => form.setValues({ source: value })}
            placeholder={{
              icon: <SourceIcon />,
              value: 'Select source',
              description: 'Choose how you want to import documents.',
            }}
          />
        </InputWrapper>

        <InputWrapper error={form.errors.destination} classNames={{ root: 'tw-flex tw-flex-col tw-gap-2' }}>
          <IntegrationSelect
            integrations={destinations}
            defaultValue={form.getValues().destination}
            onSelected={(value: string) => form.setValues({ destination: value })}
            placeholder={{
              icon: <DestinationIcon />,
              value: 'Select destination',
              description: 'Choose how you want to export documents.',
            }}
          />
        </InputWrapper>

        <NextAction text="Finish -&gt;" />
      </form>
    </Step>
  );
}
